import { LegacyRef, memo, useMemo, InputHTMLAttributes, useState, ChangeEvent, forwardRef, ReactNode } from 'react'
import Image from 'next/image'
import cn from 'classnames'
import InputErrorText from 'shared/components/input/InputErrorText'
import { InformationCircleIcon } from '@heroicons/react/24/solid'

export type Suggestion = { value: string; label: string }

export type SingleLineTextFieldProps = InputHTMLAttributes<HTMLInputElement> & {
  className?: string
  label?: string | ReactNode
  smallLabel?: string
  labelStyle?: string
  subLabel?: string
  textSize?: 'primary' | 'secondary'
  footnote?: string
  footnoteLeft?: boolean
  helperText?: string
  error?: string
  inputRef?: LegacyRef<HTMLInputElement>
  inputClass?: string
  bottomSpacing?: boolean
  asterisk?: boolean // to show asterisk next to label
  prefix?: string
  suggestions?: Suggestion[]
  onSuggestionClick?: (suggestion: Suggestion) => void
}

// eslint-disable-next-line react/display-name
const SingleLineTextField = forwardRef<HTMLLabelElement, SingleLineTextFieldProps>((props, ref) => {
  const {
    className,
    label, //
    smallLabel, //
    subLabel,
    labelStyle,
    footnote,
    footnoteLeft,
    error,
    inputRef,
    helperText,
    textSize = 'secondary',
    inputClass,
    bottomSpacing = true,
    asterisk,
    prefix,
    suggestions = [],
    onSuggestionClick,
    defaultValue,
    onKeyDown,
    ...inputProps
  } = props
  const { required, maxLength, onChange } = inputProps

  // -- highlight suggestion state
  const [highlightSuggestion, setHighlightSuggestion] = useState(-1)

  const [value, setValue] = useState<string>((defaultValue as string) || (inputProps.value as string))
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setValue(value)
    onChange && onChange(e)

    // reset highlight suggestion index
    setHighlightSuggestion(-1)
  }
  const handleInputKeyDown = (e: KeyboardEvent) => {
    // @ts-ignore
    onKeyDown && onKeyDown(e)

    // highlight suggestion when user press `ArrowDown` or `ArrowUp`
    if (!suggestions.length) return

    // increase highlight index when press arrow down
    if (e.key === 'ArrowDown') {
      e.stopPropagation() // Prevents event bubbling
      e.preventDefault() // Prevents the default form submission
      setHighlightSuggestion((prevIndex) => (prevIndex >= suggestions.length - 1 ? 0 : prevIndex + 1))
    }
    // decrease highlight index when press arrow up
    if (e.key === 'ArrowUp') {
      e.stopPropagation() // Prevents event bubbling
      e.preventDefault() // Prevents the default form submission
      setHighlightSuggestion((prevIndex) => (prevIndex <= 0 ? suggestions.length - 1 : prevIndex - 1))
    }

    // trigger `onSuggestionClick` when press enter
    if (e.key === 'Enter' && suggestions[highlightSuggestion]) {
      e.stopPropagation() // Prevents event bubbling
      e.preventDefault() // Prevents the default form submission
      onSuggestionClick?.(suggestions[highlightSuggestion])
    }
  }

  const textFieldFooter = useMemo(() => {
    if (error) return <InputErrorText message={error} />
    const footnoteText = maxLength ? `${value?.length || 0}/${maxLength} characters` : footnote
    if (footnoteText || helperText)
      return (
        <div className={cn('flex justify-between', footnoteText && !helperText && footnoteLeft && '!justify-end')}>
          {helperText && (
            <div className={cn('flex items-center space-x-1 text-[10px] font-normal text-black/40 lg:text-[12px]')}>
              <InformationCircleIcon className="size-4 text-[#999999]" />
              <span>{helperText}</span>
            </div>
          )}
          {footnoteText && <div className="text-[10px] font-normal text-black/40 lg:text-[12px]">{footnoteText}</div>}
        </div>
      )
    return bottomSpacing ? <div className="h-[26px]" /> : null
  }, [error, footnote, footnoteLeft, helperText, maxLength, value?.length, bottomSpacing])

  return (
    <label className={cn('flex flex-col space-y-2', className)} ref={ref}>
      {/* input label */}
      {(label || smallLabel) && (
        <div
          className={cn('text-[10px] font-bold tracking-[5%] text-black/50 empty:hidden lg:text-[16px]', {
            'pb-2': label,
          })}
        >
          {label && <span className={cn('text-[10px] uppercase lg:text-[16px]', labelStyle)}>{label}</span>}
          {smallLabel && (
            <span className={cn('text-[10px] uppercase text-[#0F6BFF] lg:text-[12px]', labelStyle)}>{smallLabel}</span>
          )}
          {subLabel && <span className={cn('ml-2 text-xs font-medium text-black/50', labelStyle)}>{subLabel}</span>}
          {(required || asterisk) && <span className="ml-1 text-[#00D973]">*</span>}
        </div>
      )}

      {/* text input */}
      <div
        className={cn(
          'relative rounded-[8px] border border-black/10 px-[16px] py-[16px] lg:rounded-[16px] lg:px-[20px] lg:py-[25px]',
          'custom-number-input resize-none appearance-none focus:border-black/10 ',
          'font-inter font-medium text-black placeholder-black/20',
          'flex items-center',
          props.disabled && 'bg-black/5 opacity-40',
          inputClass,
        )}
      >
        {prefix && (
          <span
            className={cn({
              'text-[15px] lg:text-[22px]': textSize === 'primary',
              'text-[12px] lg:text-[14px]': textSize === 'secondary',
            })}
          >
            {prefix}
          </span>
        )}
        <input
          ref={inputRef}
          className={cn(
            'max-w-full flex-1 border-none p-0 placeholder:text-black/30 focus:bg-transparent focus:!outline-none focus:ring-0 disabled:bg-black/[1%]',
            {
              'text-[15px] lg:text-[22px]': textSize === 'primary',
              'text-[12px] lg:text-[14px]': textSize === 'secondary',
            },
          )}
          {...inputProps}
          onChange={handleChange}
          // @ts-ignore
          onKeyDown={handleInputKeyDown}
        />

        {/* render list suggestions */}
        {suggestions?.length > 0 && (
          <div
            className={cn(
              'absolute inset-[-1px] top-full z-50 h-fit',
              'overflow-hidden rounded-b-[24px] bg-white/95 pt-1 backdrop-blur-2xl',
              'border border-t-2 border-black/10 shadow-lg',
            )}
          >
            {/* list suggestions */}
            <ul>
              {suggestions.map((suggestion, index) => (
                <li
                  key={suggestion.value}
                  className={cn('cursor-pointer border-b-0 border-gray-100 px-4 py-2 hover:bg-gray-200', {
                    'bg-gray-100': index === highlightSuggestion,
                  })}
                  onClick={(event) => {
                    event.stopPropagation() // Prevents event bubbling
                    event.preventDefault() // Prevents the default form submission
                    if (onSuggestionClick) onSuggestionClick(suggestion)
                  }}
                >
                  {suggestion.label}
                </li>
              ))}
            </ul>

            {/* powered by google text */}
            <div className="flex items-center justify-end px-[28px] py-[14px]">
              <p>Powered by</p>
              <Image
                src="/icons/google-long-logo.svg"
                alt="Google logo"
                width={68}
                height={26}
                className="ml-[8px] h-[26px] w-auto"
              />
            </div>
          </div>
        )}
      </div>

      {/* error or footnote */}
      {textFieldFooter}
    </label>
  )
})

export default memo(SingleLineTextField)
